/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicSuccessModal — what a visitor sees when an action button's write lands.
 *
 * Shown only when the button configured `on_success.reveal`. Without one, the
 * caller keeps rendering today's inline alert, so no existing public page
 * changes behaviour.
 *
 * TWO DOORS, TWO KEYS. The QR carries the opaque token and nothing else; the
 * readable code beside it is a different value. A QR is a picture of some text
 * with no encryption, so whoever photographs it can read it — that is one door,
 * and it must not also open the other. The server already enforces this by
 * returning the token only as a picture; this component never tries to read it
 * back out.
 *
 * NOTHING IS REVEALED UNTIL IT IS ASKED FOR. A pass left on screen in a queue is
 * a pass anyone behind you can photograph.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import '../../styles/PublicSuccessModal.css';

/* ── Reading the picture without a raw-HTML sink ─────────────────────────────
 *
 * The QR arrives as markup, but it is NOT injected as markup. It is parsed, the
 * two values that matter are pulled out — the viewBox and the path data — and
 * the <svg> is rebuilt by React from those. Nothing the server sent becomes
 * markup in this document.
 *
 * The earlier version of this checked the string against segno's shape with a
 * regex and injected it with dangerouslySetInnerHTML. That was defeated: the
 * HTML tokenizer treats `/` after a quoted attribute value as a self-closing
 * marker and reconsumes the next letter as an ATTRIBUTE NAME, so
 * `<path d="…"/onclick="…">` parses into a live handler while a regex looking
 * for whitespace before the name sees nothing. Confirmed firing in Chrome.
 *
 * A regex cannot win against an HTML parser. So there is no longer anything for
 * an attribute to ride in on: `d` is matched against path-data characters and
 * becomes a React prop, and every other attribute on the incoming markup is
 * discarded rather than allowlisted.
 */
const PATH_DATA = /^[\sMmLlHhVvZzCcSsQqTtAa0-9.,+-]+$/;
const VIEW_BOX = /^[\d.\s-]+$/;

export function parseQrSvg(svg) {
  if (typeof svg !== 'string' || svg.length > 64000) return null;

  let doc;
  try {
    // Parsed as XML, not HTML: XML has no tag-soup recovery, so the tokenizer
    // quirk the old check missed is a hard parse error here rather than a
    // silently-materialised attribute.
    doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  } catch (e) {
    return null;
  }
  if (!doc || doc.getElementsByTagName('parsererror').length) return null;

  const root = doc.documentElement;
  if (!root || root.nodeName !== 'svg') return null;

  const children = Array.from(root.children || []);
  if (!children.length || children.some(node => node.nodeName !== 'path')) return null;

  const viewBox = root.getAttribute('viewBox');
  if (!viewBox || !VIEW_BOX.test(viewBox)) return null;

  const ds = children.map(node => node.getAttribute('d'));
  if (ds.some(d => !d || !PATH_DATA.test(d))) return null;

  // Only the viewBox and the path data are carried across. segno emits a
  // `transform="scale(N)"` at any scale but 1, and re-emitting without it would
  // draw the code at the wrong size — silently, and still scannable-looking.
  // Refuse instead, so a changed server-side scale fails visibly.
  if (children.some(node => node.hasAttribute('transform'))
      || root.hasAttribute('transform')) return null;

  return { viewBox, ds };
}

function prefersReducedMotion() {
  try {
    return Boolean(window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) {
    // A browser without matchMedia gets the animation; it is decoration.
    return false;
  }
}

export default function PublicSuccessModal({
  open, message, subtitle, reveal, onClose,
}) {
  // Tolerates the context being absent. A public page is renderable outside a
  // ThemeProvider, and the accent has a fallback anyway — a missing theme must
  // cost the colour, never the pass the visitor came for.
  const themeContext = useTheme();
  const pathId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const dialogRef = useRef(null);
  const penRef = useRef(null);
  const returnFocusRef = useRef(null);
  const [showPass, setShowPass] = useState(false);

  const reduced = useMemo(prefersReducedMotion, []);
  const qr = useMemo(() => parseQrSvg(reveal?.qr?.svg), [reveal]);

  // With no QR there is nothing to hide behind a button: the fields ARE the
  // reveal, and making someone press a button to see a receipt is friction
  // that protects nothing.
  const hasQrStep = Boolean(reveal?.qr);
  const fieldsVisible = !hasQrStep || showPass;

  // Read through a ref so the trap effect below can depend on `open` ALONE.
  // The page passes an inline arrow, so a prop-derived callback changes
  // identity on every parent render; an effect depending on it would tear down
  // and re-run constantly, yanking focus back to the dialog while someone is
  // tabbing and leaving the restore target pointing at a detached node.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const close = useCallback(() => {
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  // Start the pen-tip from here rather than trusting the declarative begin.
  useEffect(() => {
    if (!open || reduced || !penRef.current) return;
    try {
      penRef.current.beginElement();
    } catch (e) {
      // No SMIL: the stroke still draws, it simply has no travelling head.
    }
  }, [open, reduced]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    if (dialogRef.current) dialogRef.current.focus();

    function onKeyDown(event) {
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      // Kept inside the dialog: it covers the page, so tabbing out lands on
      // controls the visitor cannot see.
      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const previous = returnFocusRef.current;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  if (!open) return null;

  const accent = themeContext?.theme?.primary || '#0B7C5A';

  return (
    <div className="arcee-pass-scrim" role="presentation">
      <div
        className="arcee-pass-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${pathId}-title`}
        tabIndex={-1}
        ref={dialogRef}
        style={{ '--arcee-pass-accent': accent }}
      >
        <div className={`arcee-pass-tick${reduced ? '' : ' is-playing'}`}>
          <svg viewBox="0 0 100 100" aria-hidden="true">
            <g className="arcee-pass-tick-group">
              <circle className="arcee-pass-punch" cx="50" cy="50" r="35" />
              <circle className="arcee-pass-ring" cx="50" cy="50" r="35" />
              <path className="arcee-pass-check" id={`${pathId}-check`}
                    d="M34 51 L45 62 L67 39" />
              {/* The pen-tip. A stroke that only reveals itself reads as a
                  progress bar; a stroke with a head that travels the path and a
                  badge that recoils after it lands reads as drawn. */}
              {!reduced && (
                <circle className="arcee-pass-pen" r="4.6">
                  {/* Chrome restarts the SMIL timeline for a freshly inserted
                      fragment, so the declarative begin does play here (measured).
                      Engines that instead run SMIL against the DOCUMENT timeline
                      would find 0.5s long past and freeze the pen at the end of
                      the path, so the effect below starts it explicitly too. */}
                  <animateMotion ref={penRef} dur="0.44s" begin="0.5s" fill="freeze">
                    <mpath href={`#${pathId}-check`} />
                  </animateMotion>
                </circle>
              )}
            </g>
          </svg>
        </div>

        <h2 className="arcee-pass-title" id={`${pathId}-title`}>{message}</h2>
        {typeof subtitle === 'string' && subtitle.trim() !== '' && (
          <p className="arcee-pass-event">{subtitle}</p>
        )}

        <div className="arcee-pass-perf" aria-hidden="true"><i /><i /></div>

        <div className="arcee-pass-stub">
          {reveal?.title && <p className="arcee-pass-stub-title">{reveal.title}</p>}

          <div className="arcee-pass-actions">
            {hasQrStep && (
              <button type="button" className="btn arcee-pass-btn-primary"
                      onClick={() => setShowPass(v => !v)}>
                {showPass
                  ? (reveal.qr.hide_label || 'Hide QR code')
                  : (reveal.qr.label || 'Show QR code')}
              </button>
            )}
            <button type="button" className="btn arcee-pass-btn-ghost" onClick={close}>
              {reveal?.done_label || 'Done'}
            </button>
          </div>

          {fieldsVisible && (
            <div className="arcee-pass-reveal">
              {showPass && qr && (
                <div className="arcee-pass-qr">
                  <svg viewBox={qr.viewBox} role="img" aria-label="Entry code">
                    {qr.ds.map((d, index) => (
                      <path key={index} d={d} fill="none"
                            stroke="currentColor" strokeWidth="1" />
                    ))}
                  </svg>
                </div>
              )}
              {showPass && hasQrStep && !qr && (
                <p className="arcee-pass-qr-failed">
                  The code below is your entry. Show it at the gate.
                </p>
              )}

              {(reveal?.fields || []).map((field, index) => (
                <div className="arcee-pass-field" data-testid="reveal-field"
                     key={`${field.label}-${index}`}>
                  <p className="arcee-pass-field-label">{field.label}</p>
                  <p className="arcee-pass-field-value">{field.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
